import React, { useState, useEffect, useRef } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
    TimePicker,
    Table,
    Switch,
    Layout,
    Menu,
    InputNumber,
} from 'antd';
import { apiRequest } from '../../../service/apiRequest';
import { showSuccess, showError } from '../../../components/errorHandler';

import type { DatePickerProps } from 'antd';
import type { TimePickerProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import type { PopconfirmProps, TableColumnsType, InputRef } from 'antd';
import { DeleteOutlined, PlusOutlined, BarcodeOutlined } from '@ant-design/icons';
import type { ChangeEvent } from 'react';

import {
    WalletOutlined,
    CreditCardOutlined,
    QrcodeOutlined,
} from '@ant-design/icons';

import type { PD } from '../../../types/paymentDetail';

const { TextArea } = Input;
const { Option } = Select;


dayjs.extend(customParseFormat);

const dateFormatList = ['DD/MM/YYYY', 'DD/MM/YY', 'DD-MM-YYYY', 'DD-MM-YY'];

const tailorOptions = ['John', 'Jane', 'Smith', 'Emily']; // example data

interface AddPaymentModalProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void;
    // initialValues?: CartItem | null;
    initialValues?: PD | null | undefined;
    isEditMode?: boolean;
    cartItems?: CartItem[];
    subtotal: number;
    pd_sale_id?: number;
    pd_sd_id?: number;
}

const cashDenominations = [
    { label: '₹1 Coin', name: 'pd_1_coin', value: 1 },
    { label: '₹2 Coin', name: 'pd_2_coin', value: 2 },
    { label: '₹5 Coin', name: 'pd_5_coin', value: 5 },
    { label: '₹10 Coin', name: 'pd_10_coin', value: 10 },
    { label: '₹20 Coin', name: 'pd_20_coin', value: 20 },
    { label: '₹10 Note', name: 'pd_10_note', value: 10 },
    { label: '₹20 Note', name: 'pd_20_note', value: 20 },
    { label: '₹50 Note', name: 'pd_50_note', value: 50 },
    { label: '₹100 Note', name: 'pd_100_note', value: 100 },
    { label: '₹200 Note', name: 'pd_200_note', value: 200 },
    { label: '₹500 Note', name: 'pd_500_note', value: 500 },
];


interface CartItem {
    sale_id: string;
    sd_id: number;
    sd_sale_id: string;
    sd_product_id: number;
    sd_ledger_id: number;
    sd_sale_type: string;
    sd_manual_job_no: string;
    sd_trail_date: string;
    sd_trail_time: string;
    sd_mobile_no: string;
    sd_delivery_date: string;
    sd_delivery_time: string;
    sd_for_custmor: string;
    sd_variant_code: string;
    sd_qty: number;
    sd_unit: number;
    sd_tailor_name: string;
    sd_measurement: string;
    sd_fabric_issued: string;
    sale_transaction_id?: string;  // ✅ Add this
    sale_net_total?: number;

    product_name: string;
    product_code: string;
}

interface ProductRow {
    key: number;
    sr: number;
    product: string;
    sd_qty: number;
    sd_unit: string;
    sd_variant_code?: string;
    sd_tailor_name?: string;
    sd_measurement?: boolean;
    sd_fabric_issued?: boolean;
    subtotal: number;
}

interface Ledger {
    ledger_id: number;
    ledger_first_name?: string;
    ledger_last_name?: string;
}

const { Sider, Content } = Layout;

type PaymentMethod = 'cash' | 'upi' | 'card';

const PaymentDetails: React.FC<AddPaymentModalProps> = ({
    visible,
    onCancel,
    onCreate,
    // initialValues = null,
    isEditMode = false,
    cartItems = [],
    subtotal,
    pd_sale_id,
    pd_sd_id,
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
    const [time, setTime] = useState<Dayjs | null>(null);
    const [cartData, setCart] = useState<CartItem | null>(null);
    const [ledgers, setLedgers] = useState<Ledger[]>([]);
    const [tailorOptions, setTailorOptions] = useState<{ value: string; label: string }[]>([]);
    const [selectedPaymentMode, setSelectedPaymentMode] = useState(null);
    const denominationFields = Form.useWatch([], form); // watches entire form
    const [initialValues, setInitialValues] = useState<PD | null>(null);
    const inputRef = useRef<InputRef>(null);


    const [isPDModalVisible, setIsPDModalVisible] = useState(false);
    const [isPDEditMode, setIsPDEditMode] = useState(false);
    const [initialValuesPD, setInitialValuesPD] = useState<PD | null>(null);
    const [selectedRecord, setSelectedRecord] = useState<CartItem | null>(null);



    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };
    const onChange: TimePickerProps['onChange'] = (time, timeString) => {
        console.log('Selected time:', timeString);
        setTime(time);
    };


    const getCashTotal = () => {
        if (!denominationFields) return 0;

        return cashDenominations.reduce((total, denom) => {
            const qty = denominationFields[denom.name] || 0;
            return total + qty * denom.value;
        }, 0);
    };

    const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>('cash');

    const [cashAmount, setCashAmount] = useState(0)
    const [upiAmount, setUpiAmount] = useState<number>(0);
    const [cardAmount, setCardAmount] = useState(0);
    const [totalCash, setTotalCash] = useState(0);

    const [cardValue, setCardValue] = useState("");
    const [nameValue, setNameValue] = useState("");
    const [upiLength, setUPILength] = useState(0);

    const totalPaid = cashAmount + upiAmount + cardAmount;
    const pendingPayment = subtotal - totalPaid;
    const refundAmount = cashAmount - Math.max(pendingPayment, 0);


    // Calculate net total
    const netTotal = upiAmount + cardAmount + cashAmount;


    useEffect(() => {
        const values = form.getFieldsValue();
        const cashTotal = cashDenominations.reduce((acc, curr) => {
            const qty = values[curr.name] || 0;
            const denom = parseInt(curr.name.match(/\d+/)?.[0] || '0');
            return acc + qty * denom;
        }, 0);
        setCashAmount(cashTotal);
    }, [form]);


    const billingTotalAmount = initialValues?.sale_net_total || 0;



    // valiation method for coin and method
    const inputNumberProps = {
        min: 0,
        placeholder: "Qty",
        style: { width: "100%" },
        controls: false,
        parser: (value?: string) => {
            const parsed = value?.replace(/[^\d]/g, "") ?? "";
            return parsed === "" ? 0 : parseInt(parsed, 10);
        },
        formatter: (value?: number | string) => {
            if (typeof value === "number") {
                return value.toString();
            }
            const number = parseInt(value ?? "", 10);
            return isNaN(number) ? "" : String(number);
        },
        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (
                ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
                    'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)
            ) {
                return;
            }
            if (!/^\d$/.test(e.key)) {
                e.preventDefault();
            }
        },
        onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
            const paste = e.clipboardData.getData('text');
            if (!/^\d+$/.test(paste)) {
                e.preventDefault();
            }
        },
    };


    // cradit card validation
    const formatCardNumber = (value: string) => {
        const digits = value.replace(/\D/g, "");
        const trimmed = digits.substring(0, 16);
        const spaced = trimmed.replace(/(.{4})/g, "$1 ").trim();

        return spaced;
    };

    const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatCardNumber(e.target.value);
        setCardValue(formatted);
        form.setFieldsValue({ card_number: formatted });
    };

    // holder name 
    const handleHolderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Allow only letters (a-z, A-Z) and spaces
        const onlyLetters = e.target.value.replace(/[^a-zA-Z\s]/g, "");
        setNameValue(onlyLetters);
        form.setFieldsValue({ card_holder: onlyLetters });
    };


    const handleSubmitPaymentDetails = async (values: any) => {
        try {
            // ✅ Get all field values, including unmounted ones
            const values = form.getFieldsValue(true);

            const isEdit = isEditMode && initialValues?.pd_id;

            const payload: Partial<PD> = {
                // Common fields
                pd_sale_id: values.pd_sale_id,
                pd_sd_id: values.pd_sd_id,

                // UPI
                pd_upi_transaction_id: values.pd_upi_transaction_id,
                pd_upi_amount: upiAmount,
                pd_method_upi: upiAmount > 0 ? 'Yes' : 'No',

                // Credit Card
                pd_cradit_card_number: values.pd_cradit_card_number,
                pd_cradit_card_holder_name: values.pd_cradit_card_holder_name,
                pd_cradit_card_amount: cardAmount,
                pd_method_cradit_card: cardAmount > 0 ? 'Yes' : 'No',

                // Cash
                pd_1_coin: values.pd_1_coin || 0,
                pd_2_coin: values.pd_2_coin || 0,
                pd_5_coin: values.pd_5_coin || 0,
                pd_10_coin: values.pd_10_coin || 0,
                pd_20_coin: values.pd_20_coin || 0,
                pd_10_note: values.pd_10_note || 0,
                pd_20_note: values.pd_20_note || 0,
                pd_50_note: values.pd_50_note || 0,
                pd_100_note: values.pd_100_note || 0,
                pd_200_note: values.pd_200_note || 0,
                pd_500_note: values.pd_500_note || 0,
                pd_cash_amount: cashAmount,
                pd_method_cash: cashAmount > 0 ? 'Yes' : 'No',

                // Totals
                pd_all_total_amount: netTotal,
                pd_sale_net_total: subtotal,
            };

            // if (isEdit) {
            //     payload.pd_id = initialValues.pd_id;
            // }


            console.log("Submit values: ", values);
            if (isEdit && initialValues && 'pd_id' in initialValues && initialValues.pd_id !== undefined) {
                payload.pd_id = initialValues.pd_id;
            }


            const url = isEdit ? `update-payment-detail/${payload.pd_id}` : 'create-payment-detail';
            const method = isEdit ? 'put' : 'post';

            const res = await apiRequest({ method, url, data: payload });

            showSuccess(
                isEdit ? 'Payment updated successfully' : 'Payment created successfully'
            );

            onCreate(res); // Notify parent
            form.resetFields();
            if (isModalClose) onCancel(); // Close modal if needed

        } catch (error) {
            console.error(error);
            showError(`Failed to ${isEditMode ? 'update' : 'create'} payment details`);
        }
    };


    // useEffect(() => {
    //     if (visible && isEditMode && initialValues) {
    //         form.setFieldsValue(initialValues);
    //     } else if (visible) {
    //         form.resetFields();
    //     }
    // }, [visible, isEditMode, initialValues]);

    useEffect(() => {
        if (visible) {
            if (isEditMode && initialValues) {
                // ✅ Edit mode: fill form with initial values
                form.setFieldsValue({
                    pd_id: initialValues?.pd_id,
                    pd_sale_id: initialValues?.pd_sale_id,
                    pd_sd_id: initialValues?.pd_sd_id,
                    pd_sale_transaction_id: initialValues?.pd_sale_transaction_id,

                    // UPI
                    pd_method_upi: initialValues?.pd_method_upi,
                    pd_upi_transaction_id: initialValues?.pd_upi_transaction_id,
                    pd_upi_amount: initialValues?.pd_upi_amount,

                    // Credit Card
                    pd_method_cradit_card: initialValues?.pd_method_cradit_card,
                    pd_cradit_card_number: initialValues?.pd_cradit_card_number,
                    pd_cradit_card_holder_name: initialValues?.pd_cradit_card_holder_name,
                    pd_cradit_card_amount: initialValues?.pd_cradit_card_amount,

                    // Cash
                    pd_method_cash: initialValues?.pd_method_cash,
                    pd_1_coin: initialValues?.pd_1_coin,
                    pd_2_coin: initialValues?.pd_2_coin,
                    pd_5_coin: initialValues?.pd_5_coin,
                    pd_10_coin: initialValues?.pd_10_coin,
                    pd_20_coin: initialValues?.pd_20_coin,
                    pd_10_note: initialValues?.pd_10_note,
                    pd_20_note: initialValues?.pd_20_note,
                    pd_50_note: initialValues?.pd_50_note,
                    pd_100_note: initialValues?.pd_100_note,
                    pd_200_note: initialValues?.pd_200_note,
                    pd_500_note: initialValues?.pd_500_note,
                    pd_cash_amount: initialValues?.pd_cash_amount,

                    // Totals
                    pd_all_total_amount: initialValues?.pd_all_total_amount,
                    pd_sale_net_total: initialValues?.pd_sale_net_total,

                    // Sale info
                    sale_id: initialValues?.sale_id,
                    sale_transaction_id: initialValues?.sale_transaction_id,
                    sale_net_total: initialValues?.sale_net_total,
                });

            } else {
                // ✅ Add mode: reset and set base values
                form.resetFields();
                form.setFieldsValue({
                    pd_sale_id,
                    pd_sd_id,
                    pd_sale_net_total: subtotal,
                });
            }
        }
    }, [visible, isEditMode, initialValues, pd_sale_id, pd_sd_id, subtotal]);




    useEffect(() => {
        if (visible) {
            form.setFieldsValue({
                pd_sale_id,
                pd_sd_id,
                pd_sale_net_total: subtotal
            });
        }
    }, [visible, pd_sale_id, pd_sd_id, subtotal]);



    // Format digits into groups of 4
    const formatCardDigitNumber = (value: string) => {
        return value.replace(/\D/g, '') // Remove non-digits
            .slice(0, 16) // Max 16 digits
            .replace(/(.{4})/g, '$1 ') // Add space every 4 digits
            .trim();
    };

    // ✅ Properly type the event
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const input = e.target;
        const rawValue = input.value;
        const digitsOnly = rawValue.replace(/\D/g, '').slice(0, 16);
        const formatted = formatCardDigitNumber(digitsOnly);

        // Preserve cursor position
        const prevLength = cardValue.length;
        const newLength = formatted.length;
        const cursorDiff = newLength - prevLength;

        setCardValue(formatted);

        // Restore caret position after formatting
        setTimeout(() => {
            const el = inputRef.current?.input; // ✅ Now TS knows input exists
            if (el) {
                const pos = input.selectionStart! + (cursorDiff > 0 ? 1 : 0);
                el.setSelectionRange(pos, pos);
            }
        }, 0);
    };



    return (
        <Modal
            title={isEditMode ? 'Edit Payment Entry' : 'Add New Payment Entry'}
            open={visible}
            onCancel={onCancel}
            footer={[
                <Button
                    type="primary"
                    htmlType="submit"
                    onClick={() => {
                        setIsModalClose(true);
                        form.submit();
                    }}
                    disabled={isSubmitDisabled()}
                >
                    Save Payment
                </Button>,
            ]}
            width="90%"
            destroyOnHidden
            maskClosable={false}
            centered
        >
            <Layout style={{ minHeight: '60vh' }}>
                {/* Sidebar for payment method */}
                <Sider width={200} theme="light" style={{ background: '#f0f2f5' }}>
                    <Menu
                        mode="inline"
                        defaultSelectedKeys={['cash']}
                        selectedKeys={[selectedMethod]}
                        style={{ height: '100%', borderRight: 0 }}
                        onClick={(e) => setSelectedMethod(e.key as PaymentMethod)}
                    >
                        <Menu.Item key="cash" icon={<WalletOutlined />}>
                            Cash
                        </Menu.Item>
                        <Menu.Item key="upi" icon={<QrcodeOutlined />}>
                            UPI
                        </Menu.Item>
                        <Menu.Item key="card" icon={<CreditCardOutlined />}>
                            Credit Card
                        </Menu.Item>
                    </Menu>
                </Sider>

                {/* Right content form */}
                <Content style={{ padding: '0 24px', minHeight: 280 }}>
                    <Form
                        form={form}
                        layout="vertical"
                        autoComplete="off"
                        onFinish={handleSubmitPaymentDetails}
                        onValuesChange={() => {
                            const values = form.getFieldsValue();

                            if (selectedMethod === 'cash') {
                                const totalCash = cashDenominations.reduce((acc, curr) => {
                                    const qty = values[curr.name] || 0;
                                    return acc + qty * curr.value;
                                }, 0);
                                setCashAmount(totalCash);
                            }

                            if (selectedMethod === 'upi') {
                                setUpiAmount(values.pd_upi_amount || 0);
                            }

                            if (selectedMethod === 'card') {
                                setCardAmount(values.pd_cradit_card_amount || 0);
                            }
                        }}

                    >
                        <Row gutter={16}>
                            <Form.Item name="pd_sale_id" hidden>
                                <Input />
                            </Form.Item>

                            <Form.Item name="pd_sd_id" hidden>
                                <Input />
                            </Form.Item>

                            {/* Render UPI Fields */}
                            {selectedMethod === 'upi' && (
                                <>
                                    <Col span={12}>
                                        <Form.Item
                                            name="pd_upi_transaction_id"
                                            label="UPI Transaction ID"
                                        // rules={[
                                        //     { required: true, message: "Enter UPI Transaction ID" },
                                        //     {
                                        //         pattern: /^[A-Z0-9]{10,20}$/, // Alphanumeric uppercase, 10 to 20 chars
                                        //         message: "Enter a valid UPI Transaction ID (10–20 characters)",
                                        //     },
                                        // ]}
                                        >
                                            <Input
                                                placeholder="Enter UPI Ref / Transaction ID"
                                                maxLength={20}
                                                onChange={(e) => setUPILength(e.target.value.length)}
                                                suffix={
                                                    <span style={{ fontSize: 12, color: "#888" }}>
                                                        {upiLength}/20
                                                    </span>
                                                }
                                                style={{ paddingRight: 40 }} // Add padding so text doesn't overlap suffix
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col span={12}>
                                        <Form.Item
                                            name="pd_upi_amount"
                                            label="UPI Amount"
                                            rules={[
                                                { required: true, message: 'Enter UPI amount' },
                                                {
                                                    type: 'number',
                                                    min: 1,
                                                    message: 'Amount must be greater than zero',
                                                },
                                            ]}
                                        >
                                            <InputNumber
                                                min={0}
                                                style={{ width: '100%' }}
                                                placeholder="Enter UPI Amount"
                                                controls={false}
                                                parser={value => {
                                                    const parsed = value?.replace(/[^\d.]/g, '') ?? '';
                                                    return parsed === '' ? 0 : parseFloat(parsed);
                                                }}
                                                formatter={value => (value ? String(value) : '0')}
                                                value={upiAmount}
                                                onChange={val => setUpiAmount(val ?? 0)}
                                                onKeyDown={(e) => {
                                                    if (
                                                        ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
                                                            'ArrowLeft', 'ArrowRight', 'Home', 'End', '.'].includes(e.key)
                                                    ) {
                                                        return;
                                                    }
                                                    if (!/^\d$/.test(e.key)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onPaste={(e) => {
                                                    const paste = e.clipboardData.getData('text');
                                                    if (!/^\d*\.?\d*$/.test(paste)) { // Allow only numbers with optional single dot
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>

                                </>
                            )}

                            {/* Render Card Fields */}
                            {selectedMethod === 'card' && (
                                <>
                                    <Col span={12}>
                                        <Form.Item
                                            name="pd_cradit_card_number"
                                            label="Card Number"
                                            rules={[
                                                { required: true, message: 'Enter card number' },
                                                {
                                                    validator: (_, value) => {
                                                        const digitsOnly = (value || '').replace(/\s/g, '');
                                                        if (digitsOnly.length !== 16) {
                                                            return Promise.reject('Card number must be 16 digits');
                                                        }
                                                        if (!/^\d+$/.test(digitsOnly)) {
                                                            return Promise.reject('Card number must contain only digits');
                                                        }
                                                        return Promise.resolve();
                                                    },
                                                },
                                            ]}
                                        >
                                            <Input
                                                ref={inputRef}
                                                maxLength={19}
                                                value={cardValue}
                                                onChange={handleChange}
                                                placeholder="XXXX XXXX XXXX XXXX"
                                                suffix={
                                                    <span style={{ fontSize: 12, color: '#888' }}>
                                                        {cardValue.replace(/\s/g, '').length}/16
                                                    </span>
                                                }
                                                onKeyDown={(e) => {
                                                    if (
                                                        ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
                                                            'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)
                                                    ) {
                                                        return;
                                                    }

                                                    if (!/^\d$/.test(e.key)) {
                                                        e.preventDefault();
                                                        return;
                                                    }

                                                    const digits = cardValue.replace(/\s/g, '');
                                                    if (digits.length >= 16) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onPaste={(e) => {
                                                    const paste = e.clipboardData.getData('text');
                                                    const clean = paste.replace(/\D/g, '');

                                                    if (!/^\d+$/.test(clean) || clean.length > 16) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                        </Form.Item>


                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="pd_cradit_card_holder_name"
                                            label="Card Holder Name"
                                            rules={[
                                                { required: true, message: "Enter card holder name" },
                                                {
                                                    pattern: /^[A-Za-z\s]+$/,
                                                    message: "Name must contain only letters and spaces",
                                                },
                                            ]}
                                        >
                                            <Input
                                                placeholder="Name on Card"
                                                value={nameValue}
                                                onChange={handleHolderNameChange}
                                                maxLength={255} // Optional max length
                                                suffix={
                                                    <span style={{ fontSize: 12, color: "#888" }}>
                                                        {nameValue.length}/255
                                                    </span>
                                                }
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="pd_cradit_card_amount"
                                            label="Card Payment Amount"
                                            rules={[
                                                { required: true, message: "Enter card amount" },
                                                {
                                                    type: "number",
                                                    min: 1,
                                                    message: "Amount must be greater than zero",
                                                },
                                            ]}
                                        >
                                            <InputNumber<number>
                                                min={0}
                                                style={{ width: "100%" }}
                                                placeholder="Enter Card Amount"
                                                controls={false}
                                                parser={(value) => {
                                                    const parsed = value?.replace(/[^\d.]/g, "") ?? "";
                                                    return parsed === "" ? 0 : parseFloat(parsed);
                                                }}
                                                formatter={(value) => {
                                                    if (typeof value === "number") {
                                                        return value.toString();
                                                    }
                                                    const number = parseFloat(value ?? "");
                                                    return isNaN(number) ? "" : String(number);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (
                                                        [
                                                            'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
                                                            'ArrowLeft', 'ArrowRight', 'Home', 'End', '.'
                                                        ].includes(e.key)
                                                    ) {
                                                        return;
                                                    }
                                                    if (!/^\d$/.test(e.key)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onPaste={(e) => {
                                                    const paste = e.clipboardData.getData('text');
                                                    if (!/^\d*\.?\d*$/.test(paste)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                        </Form.Item>

                                    </Col>
                                </>
                            )}

                            {/* Render Cash Fields */}
                            {selectedMethod === 'cash' && (
                                <Col span={24}>
                                    <h4 style={{ marginBottom: 16 }}>Cash Denominations</h4>

                                    {/* NOTES */}
                                    <h3 style={{ marginBottom: 10 }}>Notes</h3>
                                    <Row gutter={[16, 16]}>
                                        <Col xs={12} sm={6}>
                                            <Form.Item name="pd_10_note" label="₹10 Note">
                                                <InputNumber {...inputNumberProps} />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={12} sm={6}>
                                            <Form.Item name="pd_20_note" label="₹20 Note">
                                                <InputNumber {...inputNumberProps} />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={12} sm={6}>
                                            <Form.Item name="pd_50_note" label="₹50 Note">
                                                <InputNumber {...inputNumberProps} />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={12} sm={6}>
                                            <Form.Item name="pd_100_note" label="₹100 Note">
                                                <InputNumber {...inputNumberProps} />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={12} sm={6}>
                                            <Form.Item name="pd_200_note" label="₹200 Note">
                                                <InputNumber {...inputNumberProps} />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={12} sm={6}>
                                            <Form.Item name="pd_500_note" label="₹500 Note">
                                                <InputNumber {...inputNumberProps} />
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    {/* COINS */}
                                    <h3 style={{ margin: '24px 0 10px' }}>Coins</h3>
                                    <Row gutter={[16, 16]}>
                                        <Col xs={12} sm={6}>
                                            <Form.Item name="pd_1_coin" label="₹1 Coin">
                                                <InputNumber {...inputNumberProps} />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={12} sm={6}>
                                            <Form.Item name="pd_2_coin" label="₹2 Coin">
                                                <InputNumber {...inputNumberProps} />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={12} sm={6}>
                                            <Form.Item name="pd_5_coin" label="₹5 Coin">
                                                <InputNumber {...inputNumberProps} />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={12} sm={6}>
                                            <Form.Item name="pd_10_coin" label="₹10 Coin">
                                                <InputNumber {...inputNumberProps} />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={12} sm={6}>
                                            <Form.Item name="pd_20_coin" label="₹20 Coin">
                                                <InputNumber {...inputNumberProps} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Col>
                            )}


                        </Row>


                    </Form>
                </Content>
            </Layout>

            {/* Summary Section */}
            <Row justify="space-between" align="middle" style={{ marginTop: 24, padding: '0 16px' }}>
                <Col sm={24} md={8}>
                    <div style={{ fontSize: 18, fontWeight: 'bold', color: '#000' }}>
                        Total Billing Amount:  ₹{subtotal.toFixed(2)}
                    </div>

                </Col>
                <Col sm={24} md={5}>
                    <div style={{ fontSize: 16, fontWeight: 'bold', color: '#000' }}>
                        Total Cash Amount: ₹{cashAmount}
                    </div>
                </Col>
                <Col sm={24} md={5}>
                    <div style={{ fontSize: 16, fontWeight: 'bold', color: '#000' }}>
                        UPI Amount: ₹{upiAmount}
                    </div>
                </Col>
                <Col sm={24} md={5}>
                    <div style={{ fontSize: 16, fontWeight: 'bold', color: '#000' }}>
                        Credit Card Amount: ₹{cardAmount}
                    </div>
                </Col>
            </Row>





            {/* Second Row: Net Total */}
            <Row justify="end" style={{ marginTop: 12, paddingRight: 16 }}>
                <Col>
                    <div
                        style={{
                            fontSize: 18,
                            fontWeight: 'bold',
                            color: netTotal === billingTotalAmount ? '#1890ff' : 'red',
                        }}
                    >
                        Net Pending Payment: ₹{pendingPayment.toFixed(2)}
                    </div>

                </Col>
            </Row>
            {selectedMethod === 'cash' && (
                <Row justify="end" style={{ marginTop: 12, paddingRight: 16 }}>
                    <Col>
                        <div
                            style={{
                                fontSize: 16,
                                fontWeight: 'bold',
                                color: netTotal === billingTotalAmount ? '#1890ff' : 'red',
                            }}
                        >
                            Refund Amount: ₹{refundAmount.toFixed(2)}
                        </div>
                    </Col>
                </Row>
            )}


        </Modal>
    );
};

export default PaymentDetails;
